import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import Card from "../components/common/Card";
import Button from "../components/common/Button";
import { createPatient } from "../services/patientService";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import PatientInsuranceFormSections from "../components/patient/PatientInsuranceFormSections";
import { PATIENT_FORM_DEFAULTS } from "../constants/patientFormDefaults";
import { OTHER_SYMPTOM_OPTION, PATIENT_SYMPTOM_OPTIONS } from "../constants/patientSymptoms";
import {
  EMAIL_FORMAT_ERROR,
  PHONE_FORMAT_ERROR,
  isValidEmail,
  isValidPhone,
} from "../utils/validation";

export default function NewPatientPage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { push } = useToast();
  const [form, setForm] = useState(PATIENT_FORM_DEFAULTS);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const errors = useMemo(() => {
    const showReq = (key) => touched[key] || submitAttempted;
    const showContact = (key) => touched[key] || submitAttempted;
    return {
      firstName: showReq("firstName") && !form.firstName ? "First name is required" : "",
      lastName: showReq("lastName") && !form.lastName ? "Last name is required" : "",
      dateOfBirth: showReq("dateOfBirth") && !form.dateOfBirth ? "Date of birth is required" : "",
      phone: showContact("phone") && form.phone.trim() && !isValidPhone(form.phone) ? PHONE_FORMAT_ERROR : "",
      email: showContact("email") && form.email.trim() && !isValidEmail(form.email) ? EMAIL_FORMAT_ERROR : "",
    };
  }, [form, touched, submitAttempted]);

  const hasErrors = useMemo(
    () =>
      !form.firstName ||
      !form.lastName ||
      !form.dateOfBirth ||
      (form.phone.trim() !== "" && !isValidPhone(form.phone)) ||
      (form.email.trim() !== "" && !isValidEmail(form.email)),
    [form]
  );
  const hasOtherSymptom = form.symptoms.includes(OTHER_SYMPTOM_OPTION);

  const onSymptomToggle = (symptom, checked) => {
    setForm((prev) => {
      const symptoms = checked ? [...new Set([...prev.symptoms, symptom])] : prev.symptoms.filter((item) => item !== symptom);
      return {
        ...prev,
        symptoms,
        otherSymptomDetails:
          symptom === OTHER_SYMPTOM_OPTION && !checked ? "" : prev.otherSymptomDetails,
      };
    });
  };

  return (
    <AppShell title="Create Patient">
      <Card style={{ maxWidth: 780, margin: "0 auto" }}>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setSubmitAttempted(true);
            if (hasErrors) return;
            const id = await createPatient(form, { uid: user.uid, email: user.email, displayName: profile.displayName });
            push("Patient created", "success");
            navigate(`/patients/${id}`);
          }}
        >
          <div className="form-row">
            <label>First Name</label>
            <input
              required
              value={form.firstName}
              onBlur={() => setTouched((p) => ({ ...p, firstName: true }))}
              onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))}
            />
            {errors.firstName ? <div className="error-text">{errors.firstName}</div> : null}
          </div>
          <div className="form-row">
            <label>Last Name</label>
            <input
              required
              value={form.lastName}
              onBlur={() => setTouched((p) => ({ ...p, lastName: true }))}
              onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))}
            />
            {errors.lastName ? <div className="error-text">{errors.lastName}</div> : null}
          </div>
          <div className="form-row">
            <label>Date Of Birth</label>
            <input
              type="date"
              required
              value={form.dateOfBirth}
              onBlur={() => setTouched((p) => ({ ...p, dateOfBirth: true }))}
              onChange={(e) => setForm((p) => ({ ...p, dateOfBirth: e.target.value }))}
            />
            {errors.dateOfBirth ? <div className="error-text">{errors.dateOfBirth}</div> : null}
          </div>
          <div className="form-row">
            <label>Phone</label>
            <input
              value={form.phone}
              onBlur={() => setTouched((p) => ({ ...p, phone: true }))}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
            />
            {errors.phone ? <div className="error-text">{errors.phone}</div> : null}
          </div>
          <div className="form-row">
            <label>Email</label>
            <input
              type="email"
              value={form.email}
              onBlur={() => setTouched((p) => ({ ...p, email: true }))}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            />
            {errors.email ? <div className="error-text">{errors.email}</div> : null}
          </div>
          <div className="form-row">
            <label>Medical Record Number</label>
            <input value={form.mrn} onChange={(e) => setForm((p) => ({ ...p, mrn: e.target.value }))} />
          </div>
          <div className="form-row">
            <label>Symptoms (check all that apply)</label>
            <div className="symptom-checklist">
              {PATIENT_SYMPTOM_OPTIONS.map((symptom) => (
                <label key={symptom} className="symptom-option">
                  <input
                    type="checkbox"
                    className="symptom-checkbox"
                    checked={form.symptoms.includes(symptom)}
                    onChange={(e) => onSymptomToggle(symptom, e.target.checked)}
                  />
                  <span>{symptom}</span>
                </label>
              ))}
            </div>
            {hasOtherSymptom ? (
              <div style={{ marginTop: 10 }}>
                <label>Other Details</label>
                <input
                  placeholder="Enter additional symptom details"
                  value={form.otherSymptomDetails}
                  onChange={(e) => setForm((p) => ({ ...p, otherSymptomDetails: e.target.value }))}
                />
              </div>
            ) : null}
          </div>
          <PatientInsuranceFormSections form={form} setForm={setForm} />
          <div className="form-row">
            <label>Notes</label>
            <textarea
              rows={4}
              maxLength={500}
              value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            />
            <div className="muted">{form.notes.length}/500</div>
          </div>
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <Button variant="secondary" type="button" onClick={() => navigate("/patients")}>
              Cancel
            </Button>
            <Button type="submit">Create Patient</Button>
          </div>
        </form>
      </Card>
    </AppShell>
  );
}
